import {
  Chip,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  type OutlinedInputProps,
  type SxProps,
  type Theme
} from '@mui/material'
import { Box } from '@mui/system'
import { t } from 'ttag'

type NumberFormFieldProps = OutlinedInputProps & {
  unit?: string
  sx?: SxProps<Theme>
  errorText?: string
  helperText?: string
  defaultOptions?: number[]
}

export default function NumberFormField({
  unit,
  sx,
  errorText,
  helperText,
  defaultOptions,
  ...props
}: Readonly<NumberFormFieldProps>) {
  const handleChipClick = (value: number) => {
    if (props.onChange) {
      const syntheticEvent = {
        target: { value: String(value), valueAsNumber: value, name: props.name }
      } as React.ChangeEvent<HTMLInputElement>
      props.onChange(syntheticEvent)
    }
  }

  const setValueLabel = t`Set value to`

  return (
    <FormControl sx={sx} error={!!errorText} required={props.required}>
      <InputLabel htmlFor={`number-form-field-${props.name}`}>{props.label}</InputLabel>
      <OutlinedInput
        id={`number-form-field-${props.name}`}
        type="number"
        endAdornment={unit ? <InputAdornment position="end">{unit}</InputAdornment> : null}
        {...props}
      />
      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
      {helperText && <FormHelperText error={false}>{helperText}</FormHelperText>}
      {defaultOptions?.length ? (
        <Box display="flex" gap={1} mt={1} role="group" aria-label={t`Default options`}>
          {defaultOptions.map(opt => (
            <Chip
              key={opt}
              label={opt}
              onClick={() => handleChipClick(opt)}
              aria-label={`${setValueLabel} ${opt} ${unit ?? ''}`}
            />
          ))}
        </Box>
      ) : null}
    </FormControl>
  )
}
